import gsap from 'gsap'
import {DoubleSide, Group, MathUtils, Mesh, ShaderMaterial, Vector2, Vector3} from 'three'

import {diffuses, gltfs, images, load} from './assets'
import type {Diffuse} from './assetTypes'
import MouseMng from './MouseMng'
import fragmentShader from './shaders/frag'
import vertexShader from './shaders/vert'

const DEFAULT_LOOKAT = new Vector3(0.6, 0.6, 1)

export default class CopilotHead {
  group: Group = new Group()
  animationGroup_1: Group = new Group()
  animationGroup_2: Group = new Group()
  animationGroup_3: Group = new Group()

  goggleGroup: Group = new Group()
  faceGroup: Group = new Group()
  eyesMeshes: {left: Mesh | null; right: Mesh | null} = {left: null, right: null}
  targetElement: HTMLElement
  targetElementSize: Vector2 = new Vector2(1, 1)
  targetElementPos: Vector2 = new Vector2(0, 0)
  windowSize: Vector2
  meshes: Array<{mesh: Mesh; isGoggle: boolean; isFace: boolean}> = []
  uniforms: {
    fresnelBias: {value: number}
    fresnelScale: {value: number}
    fresnelPower: {value: number}
    uLightPosition: {value: Vector3}
  } = {
    fresnelBias: {value: 0},
    fresnelScale: {value: 2.0},
    fresnelPower: {value: 1.0},
    uLightPosition: {value: new Vector3(0.5, 1, -1).normalize()},
  }
  isInitTlPlayed: boolean = false
  initTl: gsap.core.Timeline
  mouseMng: MouseMng
  lookatProgress: {target: number; current: number} = {target: 0, current: 0}
  lookatTarget: Vector3 = new Vector3(0, 0, 1)
  lookatTarget_default: Vector3 = new Vector3(0, 0, 1)
  globalPosition: Vector3 = new Vector3(0, 0, 0)
  isBlinking: boolean = false
  blinkTimer: ReturnType<typeof setTimeout> | null = null

  constructor(targetElement: HTMLElement, windowSize: Vector2) {
    this.targetElement = targetElement
    this.windowSize = windowSize

    this.group.add(this.animationGroup_1)
    this.animationGroup_1.add(this.animationGroup_2)
    this.animationGroup_2.add(this.animationGroup_3)

    this.animationGroup_3.add(this.goggleGroup)
    this.animationGroup_3.add(this.faceGroup)

    this.mouseMng = new MouseMng(this.targetElementPos, this.targetElementSize)
    this.initTl = gsap.timeline({
      paused: true,
      onComplete: () => {
        this.createBlinkEyes()
      },
    })

    load(() => {
      this.init()

      this.initTl
        .add(() => {
          this.group.visible = true
        })
        .fromTo(
          this.animationGroup_1.position,
          {
            y: -2,
          },
          {
            y: 0,
            ease: 'elastic.out(1,0.5)',
            duration: 1.5,
          },
          0,
        )
        .fromTo(
          this.animationGroup_3.scale,
          {
            x: 0,
            y: 0,
            z: 0,
          },
          {
            x: 1,
            y: 1,
            z: 1,
            ease: 'expo.out',
            duration: 0.3,
          },
          0,
        )
    })
  }

  init() {
    for (const [key, diffuse] of Object.entries(diffuses)) {
      if (diffuse.texture) {
        const _mesh = gltfs.head.scene?.getObjectByName(key) as Mesh | undefined
        if (_mesh) {
          switch (key) {
            case 'Eyes':
            case 'Ears':
            case 'Goggle':
            case 'Glass': {
              const material_left = this.createMaterial(diffuse, key)
              const material_right = this.createMaterial(diffuse, key)

              const left = new Mesh(_mesh.geometry.clone(), material_left)
              const right = new Mesh(_mesh.geometry.clone(), material_right)

              left.name = `${key}_left`
              right.name = `${key}_right`
              right.geometry.scale(-1, 1, 1)

              if (key === 'Eyes') {
                left.position.copy(_mesh.position)
                right.position.copy(_mesh.position)
                right.position.x *= -1

                this.eyesMeshes.left = left
                this.eyesMeshes.right = right
              }

              this.meshes.push({
                mesh: left,
                isGoggle: diffuse.isGoggle,
                isFace: diffuse.isFace,
              })
              this.meshes.push({
                mesh: right,
                isGoggle: diffuse.isGoggle,
                isFace: diffuse.isFace,
              })

              if (diffuse.isGoggle) {
                this.goggleGroup.add(left)
                this.goggleGroup.add(right)
              }
              if (diffuse.isFace) {
                this.faceGroup.add(left)
                this.faceGroup.add(right)
              }
              break
            }
            default: {
              const material = this.createMaterial(diffuse, key)
              const newMesh = new Mesh(_mesh.geometry.clone(), material)
              newMesh.name = key
              this.meshes.push({
                mesh: newMesh,
                isGoggle: diffuse.isGoggle,
                isFace: diffuse.isFace,
              })

              newMesh.matrixWorldNeedsUpdate = true

              if (diffuse.isGoggle) this.goggleGroup.add(newMesh)
              if (diffuse.isFace) this.faceGroup.add(newMesh)
              break
            }
          }
        }
      }
    }
  }

  createMaterial(diffuse: Diffuse, key: string) {
    const isEye = key === 'Eyes'
    const isGlass = key === 'Glass'

    const dynamicUniforms = {
      uWink: {
        value: 0,
      },
    }

    const uniforms = {
      ...this.uniforms,
      map: {
        value: diffuse.texture,
      },
      matcap: {
        value: images.matcap.texture,
      },
      uFresnelIntensity: {
        value: diffuse.fresnelIntensity,
      },
      uIsEye: {
        value: isEye,
      },
      uFresnelColor: {
        value: diffuse.fresnelColor,
      },
      uFresnelPosRange: {
        value: diffuse.fresnelPosRange,
      },
      uSpecularIntensity: {
        value: diffuse.specularIntensity,
      },
      ...dynamicUniforms,
    }
    const material = new ShaderMaterial({
      vertexShader,
      fragmentShader,
      uniforms,
      transparent: true,
      side: DoubleSide,
      defines: {
        GLASS: isGlass ? 1 : 0,
      },
    })

    material.userData.uniforms = dynamicUniforms
    return material
  }

  createBlinkEyes() {
    this.isBlinking = true
    const tl = gsap.timeline({
      onComplete: () => {
        this.isBlinking = false
        const delay = MathUtils.lerp(2, 6, Math.random()) * 1000
        this.blinkTimer = setTimeout(() => {
          this.createBlinkEyes()
        }, delay)
      },
      defaults: {ease: 'power3.out'},
    })

    const type = Math.random() < 0.8 ? 'blinkType1' : 'blinkType2'

    const eyesScale = [
      this.eyesMeshes.left ? this.eyesMeshes.left.scale : new Vector3(1, 1, 1),
      this.eyesMeshes.right ? this.eyesMeshes.right.scale : new Vector3(1, 1, 1),
    ]

    if (type === 'blinkType1') {
      tl.to(eyesScale, 0.1, {y: 0.1}, 0).to(eyesScale, 0.3, {y: 1}, 0.2)
    } else {
      tl.to(eyesScale, 0.1, {y: 0.1}, 0)
        .to(eyesScale, 0.2, {y: 1}, 0.2)
        .to(eyesScale, 0.3, {y: 0.1}, 0.4)
        .to(eyesScale, 0.3, {y: 1}, 0.7)
    }
  }

  resize() {}

  onMousemove(event: MouseEvent) {
    this.mouseMng.onMousemove(event)
  }

  onTouchstart(event: TouchEvent) {
    this.mouseMng.onTouchstart(event)
  }

  update(delta: number, _delta: number) {
    const scale = 0.25
    const rect = this.targetElement.getBoundingClientRect()
    const x = rect.left + rect.width / 2 - this.windowSize.x / 2
    const y = -(rect.top + rect.height / 2 - this.windowSize.y / 2)
    this.group.position.set(x, y, -rect.width * scale * 2)

    this.targetElementPos.set(rect.left, rect.top)
    this.targetElementSize.set(rect.width, rect.height)

    const isInViewport =
      rect.bottom >= 0 && rect.right >= 0 && rect.top <= this.windowSize.y && rect.left <= this.windowSize.x

    if (!this.isInitTlPlayed && isInViewport) {
      this.isInitTlPlayed = true
      this.initTl.play()
    }

    this.group.scale.set(rect.width * scale, rect.width * scale, rect.width * scale)

    const mouseLength = this.mouseMng.pos.target.length()

    this.lookatProgress.target = MathUtils.clamp(MathUtils.mapLinear(mouseLength, 5, 2, 0, 1), 0, 1)
    if (!this.mouseMng.isMousemove) {
      this.lookatProgress.target = 0
    }

    const easing = 3

    this.lookatProgress.current +=
      (this.lookatProgress.target - this.lookatProgress.current) * Math.min(1, _delta * easing)
    this.lookatTarget_default.copy(DEFAULT_LOOKAT)

    this.lookatTarget.set(this.mouseMng.pos.current.x * 0.2, Math.max(this.mouseMng.pos.current.y, 0) * 0.2, 1)
    this.lookatTarget.lerp(this.lookatTarget_default, 1 - this.lookatProgress.current)

    const worldPosition = new Vector3()
    this.animationGroup_2.getWorldPosition(worldPosition)

    this.lookatTarget.x += worldPosition.x
    this.lookatTarget.y += worldPosition.y
    this.lookatTarget.z += worldPosition.z

    this.animationGroup_2.lookAt(this.lookatTarget)

    this.mouseMng.update(_delta)
  }
}
